import { API_BASE_URL, authHeaders } from './config';

export interface ItemReaction {
  id: string;
  sharedCartId: string;
  itemId: string;
  userId?: string;
  guestId?: string;
  reactionType: string;
  emoji: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    displayName: string;
    avatarUrl?: string;
  };
}

export interface ItemVote {
  id: string;
  sharedCartId: string;
  itemId: string;
  userId?: string;
  guestId?: string;
  voteType: VoteType;
  voteStrength: number;
  comment?: string;
  isAnonymous: boolean;
  votedAt: string;
  updatedAt: string;
  user?: {
    id: string;
    displayName: string;
    avatarUrl?: string;
  };
}

export type VoteType = 'upvote' | 'downvote' | 'neutral' | 'must_have' | 'nice_to_have' | 'skip';

export interface VotingAnalytics {
  id: string;
  sharedCartId: string;
  itemId: string;
  totalVotes: number;
  upvotes: number;
  downvotes: number;
  neutralVotes: number;
  mustHaveVotes: number;
  niceToHaveVotes: number;
  skipVotes: number;
  averageStrength: number;
  consensusScore: number;
  controversyScore: number;
  engagementScore: number;
  lastVoteAt?: string;
  updatedAt: string;
}

export interface ReactionSummary {
  id: string;
  sharedCartId: string;
  itemId: string;
  reactionCounts: Record<string, number>;
  totalReactions: number;
  topReaction?: string;
  recentReactions: Array<{
    userId?: string;
    guestId?: string;
    displayName: string;
    emoji: string;
    reactedAt: string;
  }>;
  updatedAt: string;
}

export interface AddReactionRequest {
  userId?: string;
  guestId?: string;
  reactionType: string;
  emoji: string;
}

export interface AddVoteRequest {
  userId?: string;
  guestId?: string;
  voteType: VoteType;
  voteStrength: number;
  comment?: string;
  isAnonymous?: boolean;
}

class ReactionsService {
  private baseURL: string;

  constructor() {
    this.baseURL = `${API_BASE_URL}/shared-carts`;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  }

  // Reaction methods
  async addReaction(cartId: string, itemId: string, request: AddReactionRequest): Promise<ItemReaction> {
    return this.request<ItemReaction>(`/${cartId}/items/${itemId}/reactions`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async removeReaction(cartId: string, itemId: string, guestId?: string): Promise<void> {
    const params = guestId ? `?guestId=${guestId}` : '';
    await this.request(`/${cartId}/items/${itemId}/reactions${params}`, {
      method: 'DELETE',
    });
  }

  async getItemReactions(cartId: string, itemId: string): Promise<ItemReaction[]> {
    return this.request<ItemReaction[]>(`/${cartId}/items/${itemId}/reactions`);
  }

  async getReactionSummary(cartId: string, itemId: string): Promise<ReactionSummary> {
    return this.request<ReactionSummary>(`/${cartId}/items/${itemId}/reactions/summary`);
  }

  // Voting methods
  async addVote(cartId: string, itemId: string, request: AddVoteRequest): Promise<ItemVote> {
    return this.request<ItemVote>(`/${cartId}/items/${itemId}/votes`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async removeVote(cartId: string, itemId: string, guestId?: string): Promise<void> {
    const params = guestId ? `?guestId=${guestId}` : '';
    await this.request(`/${cartId}/items/${itemId}/votes${params}`, {
      method: 'DELETE',
    });
  }

  async getItemVotes(cartId: string, itemId: string): Promise<ItemVote[]> {
    return this.request<ItemVote[]>(`/${cartId}/items/${itemId}/votes`);
  }

  async getVotingAnalytics(cartId: string, itemId: string): Promise<VotingAnalytics> {
    return this.request<VotingAnalytics>(`/${cartId}/items/${itemId}/votes/analytics`);
  }

  // Activity feed
  async getVotingActivityFeed(cartId: string, limit: number = 50): Promise<any[]> {
    return this.request<any[]>(`/${cartId}/activity/voting?limit=${limit}`);
  }
}

// Predefined reaction types with emojis
export const REACTION_TYPES = {
  love: { emoji: '❤️', label: 'Love it', color: '#ef4444' },
  fire: { emoji: '🔥', label: 'Fire', color: '#f97316' },
  wow: { emoji: '😍', label: 'Amazing', color: '#8b5cf6' },
  like: { emoji: '👍', label: 'Like', color: '#10b981' },
  laugh: { emoji: '😂', label: 'Funny', color: '#f59e0b' },
  meh: { emoji: '😐', label: 'Meh', color: '#6b7280' },
  dislike: { emoji: '👎', label: 'Dislike', color: '#ef4444' },
  sad: { emoji: '😢', label: 'Sad', color: '#3b82f6' },
} as const;

export type ReactionType = keyof typeof REACTION_TYPES;

// Vote type configurations
export const VOTE_TYPES = {
  upvote: { label: 'Upvote', icon: '👍', color: '#10b981', description: 'I like this' },
  downvote: { label: 'Downvote', icon: '👎', color: '#ef4444', description: 'I don\'t like this' },
  neutral: { label: 'Neutral', icon: '🤷', color: '#6b7280', description: 'I\'m neutral' },
  must_have: { label: 'Must Have', icon: '⭐', color: '#f59e0b', description: 'We need this' },
  nice_to_have: { label: 'Nice to Have', icon: '✨', color: '#8b5cf6', description: 'Would be nice' },
  skip: { label: 'Skip', icon: '⏭️', color: '#6b7280', description: 'Let\'s skip this' },
} as const;

export const reactionsService = new ReactionsService();